/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import { api, getErrorMessage } from '../lib/axios';

interface LoginCredentials {
  email: string;
  password: string;
}

interface LoginResponse {
  success: boolean;
  token?: string;
  user?: {
    id: string;
    email: string;
    fullName: string;
    roleLevel: number;
    manufacturerId: string | null;
    status: string;
    isOperationManager?: boolean;
    operationManagerId?: string | null;
    operationManagerStatus?: string | null;
  };
  error?: string;
}

interface RegisterData {
  email: string;
  password: string;
  fullName: string;
  phone: string;
  manufacturerId: string;
  roleLevel: number;
  parentUserId?: string;
}

interface RegisterResponse {
  success: boolean;
  user?: {
    id: string;
    email: string;
    fullName: string;
    status: string;
  };
  error?: string;
}

export const authService = {
  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
      };
    }
  },

  /**
   * Register new user
   */
  async register(data: RegisterData): Promise<RegisterResponse> {
    try {
      const response = await api.post('/users/register', data);
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: getErrorMessage(error),
      };
    }
  },

  /**
   * Logout user
   */
  async logout(): Promise<void> {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    }
  },

  /**
   * Get current user
   */
  async getCurrentUser() {
    try {
      const response = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Verify token
   */
  async verifyToken(token: string) {
    try {
      const response = await api.post('/auth/verify', { token });
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },

  /**
   * Check if manufacturer needs to complete registration
   */
  async checkRegistrationStatus() {
    try {
      const response = await api.get('/registration/status');
      return response.data;
    } catch (error) {
      throw new Error(getErrorMessage(error));
    }
  },
};
